import { useContext } from "react";
import { TrendingUp, ShoppingBag, Package, Banknote, Target, Receipt, AlertTriangle, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { formatterCurrency } from "../../../shared/utils";
import { DashboardContext } from "../context/DashboardContext";


export default function DataResume() {

    const { data } = useContext(DashboardContext);

    const lucro = (data.total_geral.total_produtor || 0) - (data.total_geral.total_investido || 0);
    const investido = data.total_geral.total_investido || 0;
    const ticketMedio = data.total_geral.ticket_medio || 0;
    const roi = data.total_geral.roi || 0;
    const taxaReembolso = data.total_geral.taxa_reembolso || 0;
    const reembolsos = data.reembolsos || { quantidade: 0, total_valor: 0 };
    const delta = data.delta_pct || {};

    const lucroPositive = lucro >= 0;
    const roiPositive = roi >= 0;


    return (
        <div className="space-y-4 mb-6 animate-slide-up">

            {/* HERO BENTO ROW — Lucro grande + ROI + Ticket */}
            <div className="grid grid-cols-12 gap-4">

                {/* Lucro — hero */}
                <div className="glow-card p-6 col-span-12 md:col-span-6 xl:col-span-5">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <span className="h-8 w-8 rounded-lg bg-positive-500/15 ring-1 ring-positive-500/25 grid place-items-center">
                                <Banknote className="h-4 w-4 text-positive-400" />
                            </span>
                            <span className="label-overline">Lucro Líquido</span>
                        </div>
                        <Delta value={delta.lucro} positiveIsGood />
                    </div>
                    <div className={`font-display text-4xl xl:text-5xl font-bold tracking-tight tabular-nums ${lucroPositive ? "text-ink-1" : "text-danger-400"}`}>
                        {formatterCurrency(lucro)}
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-sm">
                        <span className="text-ink-4">Investido em ads</span>
                        <span className="font-mono text-ink-2 tabular-nums">{formatterCurrency(investido)}</span>
                    </div>
                </div>

                {/* ROI */}
                <div className="surface-card surface-card-hover p-6 col-span-6 md:col-span-3 xl:col-span-3 relative overflow-hidden">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <span className="h-8 w-8 rounded-lg bg-brand-500/15 ring-1 ring-brand-500/25 grid place-items-center">
                                <Target className="h-4 w-4 text-brand-300" />
                            </span>
                            <span className="label-overline">ROI</span>
                        </div>
                        <Delta value={delta.roi} positiveIsGood unit="pp" />
                    </div>
                    <div className={`font-display text-3xl font-bold tabular-nums ${roiPositive ? "text-positive-400" : "text-danger-400"}`}>
                        {roi.toFixed(1)}%
                    </div>
                    <p className="text-xs text-ink-4 mt-2">Lucro líquido / investido em ads</p>
                </div>

                {/* Ticket Médio */}
                <div className="surface-card surface-card-hover p-6 col-span-6 md:col-span-3 xl:col-span-2">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <span className="h-8 w-8 rounded-lg bg-accent-sky/15 ring-1 ring-accent-sky/25 grid place-items-center">
                                <Receipt className="h-4 w-4 text-accent-sky" />
                            </span>
                            <span className="label-overline">Ticket</span>
                        </div>
                        <Delta value={delta.ticket_medio} positiveIsGood />
                    </div>
                    <div className="font-display text-2xl xl:text-3xl font-bold text-ink-1 tabular-nums">
                        {formatterCurrency(ticketMedio)}
                    </div>
                    <p className="text-xs text-ink-4 mt-2">Líquido por transação</p>
                </div>

                {/* Reembolsos */}
                <div className="surface-card surface-card-hover p-6 col-span-12 md:col-span-12 xl:col-span-2">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <span className="h-8 w-8 rounded-lg bg-danger-500/15 ring-1 ring-danger-500/25 grid place-items-center">
                                <AlertTriangle className="h-4 w-4 text-danger-400" />
                            </span>
                            <span className="label-overline">Reembolsos</span>
                        </div>
                        <Delta value={delta.reembolsos_qtd} positiveIsGood={false} />
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className="font-display text-2xl xl:text-3xl font-bold text-danger-400 tabular-nums">
                            {reembolsos.quantidade || 0}
                        </span>
                        <span className="text-xs text-ink-4 tabular-nums">{taxaReembolso.toFixed(1)}%</span>
                    </div>
                    <p className="text-xs text-ink-4 mt-2 truncate">{formatterCurrency(reembolsos.total_valor || 0)}</p>
                </div>
            </div>

            {/* SECONDARY ROW — Principais / Orderbumps / Total Geral */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                <BreakdownCard
                    icon={<Package className="h-4 w-4 text-brand-300" />}
                    iconBg="bg-brand-500/15 ring-brand-500/25"
                    label="Produtos Principais"
                    deltaValue={delta.principais_qtd}
                    deltaPositiveIsGood
                    rows={[
                        ["Quantidade", data.principais.quantidade, false],
                        ["Bruto (cliente paga)", formatterCurrency(data.principais.total_valor), false],
                        ["Líquido (produtor recebe)", formatterCurrency(data.principais.total_produtor), true],
                    ]}
                />

                <BreakdownCard
                    icon={<ShoppingBag className="h-4 w-4 text-warning-400" />}
                    iconBg="bg-warning-500/15 ring-warning-500/25"
                    label="Orderbumps"
                    deltaValue={delta.orderbumps_qtd}
                    deltaPositiveIsGood
                    rows={[
                        ["Quantidade", data.orderbumps.quantidade, false],
                        ["Bruto (cliente paga)", formatterCurrency(data.orderbumps.total_valor), false],
                        ["Líquido (produtor recebe)", formatterCurrency(data.orderbumps.total_produtor), true],
                    ]}
                />

                <BreakdownCard
                    icon={<TrendingUp className="h-4 w-4 text-positive-400" />}
                    iconBg="bg-positive-500/15 ring-positive-500/25"
                    label="Total Geral"
                    rows={[
                        ["Bruto (cliente paga)", formatterCurrency(data.total_geral.total_valor), false],
                        ["Líquido (produtor recebe)", formatterCurrency(data.total_geral.total_produtor), true],
                    ]}
                />
            </div>
        </div>
    );
}


function BreakdownCard({ icon, iconBg, label, rows, deltaValue, deltaPositiveIsGood }) {
    return (
        <div className="surface-card surface-card-hover p-5">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <span className={`h-8 w-8 rounded-lg ring-1 ${iconBg} grid place-items-center`}>
                        {icon}
                    </span>
                    <span className="label-overline">{label}</span>
                </div>
                {deltaValue !== undefined && <Delta value={deltaValue} positiveIsGood={deltaPositiveIsGood} />}
            </div>
            <dl className="space-y-2">
                {rows.map(([k, v, emphasized], i) => (
                    <div key={i} className="flex items-baseline justify-between">
                        <dt className="text-xs text-ink-4">{k}</dt>
                        <dd className={`tabular-nums ${emphasized ? "font-display text-lg font-semibold text-ink-1" : "text-sm text-ink-2"}`}>
                            {v}
                        </dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}


function Delta({ value, positiveIsGood = true, unit = "%" }) {
    if (value === null || value === undefined || !isFinite(value)) {
        return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs text-ink-4 bg-white/[0.04]" title="Sem dado no período anterior">
                <Minus className="h-3 w-3" />
                <span className="tabular-nums">—</span>
            </span>
        );
    }
    const rounded = Math.abs(value) < 0.05 ? 0 : value;
    const isUp = rounded > 0;
    const isFlat = rounded === 0;
    const isGood = isFlat ? null : (isUp === positiveIsGood);
    const formatted = `${isUp ? "+" : ""}${rounded.toFixed(1)}${unit === "pp" ? " pp" : "%"}`;

    let cls = "text-ink-3 bg-white/[0.04]";
    if (isGood === true) cls = "text-positive-400 bg-positive-500/10 ring-1 ring-positive-500/20";
    else if (isGood === false) cls = "text-danger-400 bg-danger-500/10 ring-1 ring-danger-500/20";

    const Icon = isFlat ? Minus : (isUp ? ArrowUpRight : ArrowDownRight);

    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs font-medium ${cls}`} title="vs período anterior de mesma duração">
            <Icon className="h-3 w-3" />
            <span className="tabular-nums">{formatted}</span>
        </span>
    );
}
